import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '../lib/cn.js';
import { AnchoredList, useAnchor } from './anchored-list.js';
import { GRID_CELL_CONTROL } from './EntryGrid.js';

/**
 * A select you can type into.
 *
 * A five-level chart of accounts is a few thousand rows, and a native `<select>`
 * over it is unusable: the only way to reach `6420 Vehicle & Fuel` is to scroll
 * past every asset and liability in the book, and the browser's own type-ahead
 * matches the *label* from its first character, so typing "vehicle" finds
 * nothing because the option starts with "6420". This filters on every word, in
 * either order, across the code and the name — `veh`, `6420` and `fuel vehicle`
 * all find the same row.
 *
 * **The list is portalled, and it has to be** — see `anchored-list.tsx`, which
 * owns that escape hatch for this and for the invoice scan bar.
 *
 * Keyboard: type to filter, ↑/↓ to move, Enter or Tab to take the highlighted
 * row, Escape to abandon and restore what was there. Blurring without choosing
 * restores too — a half-typed filter is not a value.
 */

export interface ComboboxOption {
  value: string;
  /** The row's primary text: `1131 · Accounts Receivable`. */
  label: string;
  /** Dimmer trailing text: the level, the control kind, a balance. */
  hint?: string;
  /** Collects rows under a heading, in the order the options are given. */
  group?: string;
  disabled?: boolean;
  /** Matched by the search but never displayed — a short name, an old code. */
  keywords?: string;
}

export interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly ComboboxOption[];
  placeholder?: string;
  /** Shown when the filter matches nothing. */
  emptyText?: string;
  /** A line above the list: `Sub-accounts of 1131 Accounts Receivable`. */
  caption?: string;
  disabled?: boolean;
  invalid?: boolean;
  /**
   * `grid` borrows the chrome-less cell control from `EntryGrid`; `field` is the
   * bordered control that matches `Input` and `Select` in a form or a toolbar.
   */
  variant?: 'grid' | 'field';
  className?: string;
  id?: string;
  'aria-label'?: string;
}

/** Bordered variant — matches `Input`/`Select` at `size="sm"`. */
const FIELD_CONTROL = cn(
  'h-8 w-full rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)]',
  'px-2.5 text-[12.5px] text-[var(--ink)] outline-none',
  'transition-colors duration-[var(--dur-fast)]',
  'focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-soft)]',
  'disabled:cursor-not-allowed disabled:bg-[var(--surface-2)] disabled:text-[var(--ink-3)]',
  'aria-[invalid=true]:border-[var(--debit)]',
);

/**
 * Every word of the query must appear somewhere in the row.
 *
 * Word-wise rather than substring so `fuel vehicle` finds `Vehicle & Fuel`,
 * which is what somebody who half-remembers an account name actually types.
 */
function matches(option: ComboboxOption, terms: readonly string[]): boolean {
  if (terms.length === 0) return true;
  const hay = `${option.value} ${option.label} ${option.hint ?? ''} ${option.keywords ?? ''}`
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return terms.every((term) => hay.includes(term));
}

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    value,
    onChange,
    options,
    placeholder = 'Search…',
    emptyText = 'Nothing matches',
    caption,
    disabled,
    invalid,
    variant = 'grid',
    className,
    id,
    'aria-label': ariaLabel,
  },
  forwardedRef,
) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const { ref: inputRef, anchor } = useAnchor(open);
  const listRef = useRef<HTMLDivElement>(null);

  // The grid focuses the account cell of a newly added line, so the input has
  // to be reachable from outside while the ref stays available in here for
  // measuring the anchor.
  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement, []);

  const selected = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const terms = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);

  const shown = useMemo(() => options.filter((option) => matches(option, terms)), [options, terms]);

  /**
   * The value shown in the box.
   *
   * While the list is open it is whatever is being typed — including the empty
   * string, so the placeholder can show the current selection and clearing the
   * box reveals the whole list rather than re-filtering on a stale label.
   *
   * Closed, it falls back to the raw value when no option matches. A code that
   * is set but not offered — an account deactivated since the voucher was
   * written, a row from an import — must still be visible: rendering it as an
   * empty box would say the field is unset when it is not, and the next save
   * would be made against something the accountant could not see.
   */
  const display = open ? query : (selected?.label ?? value);

  // Keep the highlight in view while arrowing through a long chart.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  const commit = useCallback(
    (option: ComboboxOption | undefined) => {
      if (!option || option.disabled) return;
      onChange(option.value);
      close();
    },
    [onChange, close],
  );

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    // Start on the current selection so ↓ moves off it rather than jumping to
    // the top of a two-thousand-row list.
    const index = shown.findIndex((option) => option.value === value);
    setActive(index >= 0 ? index : 0);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        openList();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => nextEnabled(shown, i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => nextEnabled(shown, i, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(nextEnabled(shown, -1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActive(nextEnabled(shown, shown.length, -1));
        break;
      case 'Enter':
        event.preventDefault();
        commit(shown[active]);
        break;
      case 'Tab':
        // Tab commits rather than abandons: moving to the amount cell with a
        // highlighted account and losing it would be a data-entry trap.
        if (shown[active]) commit(shown[active]);
        else close();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      default:
        break;
    }
  };

  const control = (
    <input
      ref={inputRef}
      id={id}
      type="text"
      role="combobox"
      autoComplete="off"
      spellCheck={false}
      aria-label={ariaLabel}
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-autocomplete="list"
      aria-invalid={invalid || undefined}
      disabled={disabled}
      value={display}
      placeholder={open ? (selected?.label ?? placeholder) : placeholder}
      onChange={(event) => {
        setQuery(event.target.value);
        setActive(0);
        if (!open) setOpen(true);
      }}
      onMouseDown={() => {
        if (!open) openList();
      }}
      onFocus={() => {
        if (!open) openList();
      }}
      onBlur={(event) => {
        // A click inside the portalled list moves focus there; that is not a
        // dismissal, and closing on it would beat the click to the option.
        if (listRef.current?.contains(event.relatedTarget as Node)) return;
        close();
      }}
      onKeyDown={onKeyDown}
      className={cn(
        variant === 'grid' ? GRID_CELL_CONTROL : FIELD_CONTROL,
        'cursor-pointer',
        className,
      )}
    />
  );

  return (
    <>
      {control}
      {open && anchor && (
        <AnchoredList anchor={anchor} id={listId} listRef={listRef}>
          <>
            {caption && (
              <div className="px-2.5 pt-1 pb-1.5 text-[11px] text-[var(--ink-3)]">{caption}</div>
            )}

            {shown.length === 0 ? (
              <div className="px-2.5 py-2 text-[12px] text-[var(--ink-3)]">{emptyText}</div>
            ) : (
              shown.map((option, index) => {
                const previous = shown[index - 1];
                const heading = option.group && option.group !== previous?.group;

                return (
                  <div key={option.value}>
                    {heading && (
                      <div
                        className={cn(
                          'px-2.5 pt-2 pb-1 text-[10px] font-medium tracking-[0.08em] uppercase',
                          'text-[var(--ink-3)]',
                        )}
                      >
                        {option.group}
                      </div>
                    )}
                    <div
                      role="option"
                      data-index={index}
                      aria-selected={option.value === value}
                      aria-disabled={option.disabled || undefined}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => commit(option)}
                      className={cn(
                        'flex items-baseline gap-2 px-2.5 py-1.5 text-[12.5px]',
                        option.disabled
                          ? 'cursor-not-allowed text-[var(--ink-3)]'
                          : 'cursor-pointer text-[var(--ink)]',
                        index === active && !option.disabled && 'bg-[var(--accent-soft)]',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                      {option.hint && (
                        <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
                          {option.hint}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        </AnchoredList>
      )}
    </>
  );
});

/** The next selectable row in `direction`, skipping disabled ones. */
function nextEnabled(options: readonly ComboboxOption[], from: number, direction: 1 | -1): number {
  for (let i = from + direction; i >= 0 && i < options.length; i += direction) {
    if (!options[i]?.disabled) return i;
  }
  return from >= 0 && from < options.length ? from : 0;
}
